import { defaultsDeep } from 'lodash-es';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { fetchDataFromBuilder, getBuilderCacheBusterKey } from 'src/scripts/utils/builder';

export interface AccountModalConfig {
  accountModalSettings: AccountModalSettings;
  successModalSettings: SuccessModalSettings;
  loginPageSettings: LoginPageSettings;
}

interface LoginPageSettings {
  signInHeroDesktop: string;
  signUpHeroDesktop: string;
  signInHeroMobile: string;
  signUpHeroMobile: string;
}
interface AccountModalSettings {
  autoShow: boolean;
  showNewsLetterCheckbox?: boolean;
  closeButtonColor: string;
  shouldEnableOtpSignIn?: boolean;
  emailOtpVerifyTitle?: string;
  emailOtpUseCodeCta?: string;
  emailOtpUsePasswordCta?: string;
  shouldShowBirthdayInput?: boolean;
  birthdayInputLabel?: string;
  createAccountHeroImage?: string;
  signInHeroImage?: string;
  createAccountCta?: string;
  signInCta?: string;
  autoApplyDiscountCode?: string;
}

export interface AccountModalConfigContextValue extends AccountModalConfig {
  loading: boolean;
}

const DEFAULT_BENEFITS = [
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_welcome_gift.png?v=1727989867',
    text: 'Welcome Gift',
    tiers: ['tier1'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_birthday_gift.png?v=1727989867',
    text: 'Birthday Gift',
    tiers: ['tier1'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_early_access_drop.png?v=1727989868',
    text: 'Early Access to Drops',
    tiers: ['tier1'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_member_only_sale.png?v=1727989867',
    text: 'Member-Only Sales',
    tiers: ['tier1'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_free_2day_shipping.png?v=1727989867',
    text: 'Free Two-Day Shipping',
    tiers: ['tier2'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_early_access_drop.png?v=1727989868',
    text: 'Priority Access to Drops',
    tiers: ['tier2'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_a_list_events.png?v=1727989867',
    text: 'A-List Events + Classes',
    tiers: ['tier2'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_surprise_reward.png?v=1727989867',
    text: 'Surprise Reward Days',
    tiers: ['tier2'],
  },
  {
    image:
      'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_priority_customer_service.png?v=1727989867',
    text: 'Priority Customer Service',
    tiers: ['tier2'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_invite_only_experiences.png?v=1727989867',
    text: 'Invite-Only Experiences',
    tiers: ['tier3'],
  },
  {
    image: 'https://cdn.shopify.com/s/files/1/2185/2813/files/aloaccess_icons_instagram_access.png?v=1727989867',
    text: 'Unlock Private Instagram Access',
    tiers: ['tier3'],
  },
];

const DEFAULT_ACCOUNT_MODAL_CONFIG: AccountModalConfig = {
  accountModalSettings: {
    autoShow: false,
    showNewsLetterCheckbox: false,
    closeButtonColor: 'var(--white)',
    shouldEnableOtpSignIn: false,
    emailOtpVerifyTitle: 'Enter your 6-digit verification code',
    emailOtpUseCodeCta: 'Sign in with a code instead',
    emailOtpUsePasswordCta: 'Sign in with password instead',
    shouldShowBirthdayInput: false,
    birthdayInputLabel: 'Birthday',
    createAccountHeroImage: '',
    signInHeroImage: '',
    createAccountCta: '',
    signInCta: '',
    autoApplyDiscountCode: '',
  },
  successModalSettings: {
    showOnce: true,
    heroImage: '',
    titleBeforeLogo: 'Congrats',
    titleAfterLogo: 'Member',
    benefitsTitle: 'THESE BENEFITS ARE NOW YOURS:',
    ctaText: 'View your access pass',
    ctaUrl: '/pages/access',
    closeCtaEnable: true,
    closeCtaText: 'Continue',
    closeCtaTextColor: '',
    closeCtaUrl: '',
    benefits: DEFAULT_BENEFITS,
    discountCode: '',
    discountTitle: 'Use this code for 10% off:',
    discountDetails: "You've unlocked early access to our holiday sale by being a member!",
  },
  loginPageSettings: {
    signInHeroDesktop:
      'https://cdn.builder.io/api/v1/image/assets%2F6a8df35ed6394582be7e39d861128afb%2F6629d94cf3704c1d92cee847eb10663b',
    signUpHeroDesktop:
      'https://cdn.builder.io/api/v1/image/assets%2F6a8df35ed6394582be7e39d861128afb%2F36f77be4f47644d08a6707247b1888a6',
    signInHeroMobile:
      'https://cdn.builder.io/api/v1/image/assets%2F6a8df35ed6394582be7e39d861128afb%2Fa2a7240eac2c43af8e3ca72fbcb522e4',
    signUpHeroMobile:
      'https://cdn.builder.io/api/v1/image/assets%2F6a8df35ed6394582be7e39d861128afb%2F6e632cdaae964c41875de669208e58b8',
  },
};

const AccountModalConfigContext = React.createContext<AccountModalConfigContextValue>({
  ...DEFAULT_ACCOUNT_MODAL_CONFIG,
  loading: true,
});

export const AccountModalConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [config, setConfig] = useState<AccountModalConfig>(DEFAULT_ACCOUNT_MODAL_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async function () {
      try {
        const data = await fetchDataFromBuilder<AccountModalConfigResponse>('account-modal', {
          options: { cb: getBuilderCacheBusterKey() },
        }).then((resp) => resp?.data || null);

        // do nothing if no data returned
        if (data === null) return;

        if (typeof data === 'object' && !Array.isArray(data)) {
          setConfig(formatAccountModalConfigData(data));
        } else {
          console.error(
            '[AccountModalConfigProvider] - Invalid config data received from Builder for account-modal:',
            data
          );
        }
      } catch (error) {
        console.error('[AccountModalConfigProvider] - Error fetching account modal config from Builder', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  const value = useMemo(
    () => ({
      ...config,
      loading,
    }),
    [config, loading]
  );

  return <AccountModalConfigContext.Provider value={value}>{children}</AccountModalConfigContext.Provider>;
};

export const useAccountModalConfig = () => useContext(AccountModalConfigContext);

const formatAccountModalConfigData = (data: AccountModalConfigResponse): AccountModalConfig => {
  try {
    const { account, success, loginPage } = data;
    const { accountModalSettings, successModalSettings, loginPageSettings } = DEFAULT_ACCOUNT_MODAL_CONFIG;
    return {
      accountModalSettings: {
        autoShow: account?.showSignInSignUpModalEveryTime ?? accountModalSettings.autoShow,
        closeButtonColor: account?.closeButtonColor ?? accountModalSettings.closeButtonColor,
        shouldEnableOtpSignIn: account?.enableEmailOtpSignIn ?? accountModalSettings.shouldEnableOtpSignIn,
        emailOtpVerifyTitle: account?.otpVerifyTitle ?? accountModalSettings.emailOtpVerifyTitle,
        emailOtpUseCodeCta: account?.otpUseCodeCta ?? accountModalSettings.emailOtpUseCodeCta,
        emailOtpUsePasswordCta: account?.otpUsePasswordCta ?? accountModalSettings.emailOtpUsePasswordCta,
        shouldShowBirthdayInput: account?.showBirthdayInput ?? accountModalSettings.shouldShowBirthdayInput,
        birthdayInputLabel: account?.birthdayInputLabel ?? accountModalSettings.birthdayInputLabel,
        createAccountHeroImage: account?.createAccountHeroImage ?? accountModalSettings.createAccountHeroImage,
        signInHeroImage: account?.signInHeroImage ?? accountModalSettings.signInHeroImage,
        createAccountCta: account?.createAccountCta ?? accountModalSettings.createAccountCta,
        signInCta: account?.signInCta ?? accountModalSettings.signInCta,
        autoApplyDiscountCode: account?.autoApplyDiscountCode ?? accountModalSettings.autoApplyDiscountCode,
        showNewsLetterCheckbox: account?.showNewsLetterCheckbox ?? accountModalSettings.showNewsLetterCheckbox,
      },
      successModalSettings: {
        showOnce: success?.showUponSignUp ?? successModalSettings.showOnce,
        heroImage: success?.createAccountHeroImage ?? successModalSettings.heroImage,
        titleBeforeLogo: success?.titleBeforeLogo ?? successModalSettings.titleBeforeLogo,
        titleAfterLogo: success?.titleAfterLogo ?? successModalSettings.titleAfterLogo,
        benefitsTitle: success?.benefitsTitle ?? successModalSettings.benefitsTitle,
        ctaText: success?.buttonCta ?? successModalSettings.ctaText,
        ctaUrl: success?.buttonCtaLink ?? successModalSettings.ctaUrl,
        closeCtaEnable: success?.enableDisableCloseCta ?? successModalSettings.closeCtaEnable,
        closeCtaText: success?.closeCtaText ?? successModalSettings.closeCtaText,
        closeCtaTextColor: success?.closeButtonColor ?? successModalSettings.closeCtaTextColor,
        closeCtaUrl: success?.closeCtaUrl ?? successModalSettings.closeCtaUrl,
        benefits: success?.benefitsListPairs ?? successModalSettings.benefits,
        discountCode: success?.discountCode ?? successModalSettings.discountCode,
        discountTitle: success?.discountTitle ?? successModalSettings.discountTitle,
        discountDetails: success?.discountDetails ?? successModalSettings.discountDetails,
      },
      loginPageSettings: defaultsDeep(loginPage, loginPageSettings),
    };
  } catch (error) {
    console.error('[AccountModalConfigProvider] - Error formatting account modal config data from Builder', error);
    return DEFAULT_ACCOUNT_MODAL_CONFIG;
  }
};

interface AccountModalConfigResponse {
  account?: AccountModalConfigAccount;
  success?: AccountModalConfigSuccess;
  loginPage?: LoginPageSettings;
}

interface AccountModalConfigSuccess {
  showUponSignUp?: boolean;
  createAccountHeroImage?: string;
  titleBeforeLogo?: string;
  titleAfterLogo?: string;
  benefitsTitle?: string;
  buttonCta?: string;
  buttonCtaLink?: string;
  enableDisableCloseCta?: boolean;
  closeCtaText?: string;
  closeButtonColor?: string;
  closeCtaUrl?: string;
  benefitsListPairs?: SuccessModalBenefit[];
  discountCode?: string;
  discountTitle?: string;
  discountDetails?: string;
}
interface AccountModalConfigAccount {
  showSignInSignUpModalEveryTime?: boolean;
  showNewsLetterCheckbox?: boolean;
  closeButtonColor?: string;
  enableEmailOtpSignIn?: boolean;
  otpVerifyTitle?: string;
  otpUseCodeCta?: string;
  otpUsePasswordCta?: string;
  showBirthdayInput?: boolean;
  birthdayInputLabel?: string;
  autoApplyDiscountCode?: string;
  createAccountHeroImage?: string;
  signInHeroImage?: string;
  createAccountCta?: string;
  signInCta?: string;
}
